import {
  PDF_PAGE,
  createPainter,
  drawFooter,
  drawHeaderBand,
  drawMetricChips,
  drawSectionHeader,
  getPdfPalette,
  wrapText,
} from '../../../../shared/pdf/pdfTheme'
import {getDataUrlImageHex, loadTenantLogoImage} from '../../../../shared/pdf/pdfTenantLogo'

const {width: PAGE_WIDTH, height: PAGE_HEIGHT, marginX: MARGIN_X, marginBottom: MARGIN_BOTTOM} = PDF_PAGE
const VISUAL_CARD_HEIGHT = 316
const COVER_VISUAL_CARD_HEIGHT = 270

const buildPdfLandName = ({fieldName, landName}) => {
  const cleanFieldName = String(fieldName || '').trim()
  const cleanLandName = String(landName || '').trim()

  if (!cleanFieldName || !cleanLandName) {
    return cleanLandName
  }

  const prefixPattern = new RegExp(`^${cleanFieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*-\\s*`, 'i')
  return cleanLandName.replace(prefixPattern, '').trim() || cleanLandName
}

const normalizeTechnicalReportFileName = (reportName) => {
  const value = String(reportName || '').trim()
  const slugMatch = value.match(/^reporte-tecnico-agronomico-(\d{2})-(\d{2})-(\d{4})$/i)

  if (slugMatch) {
    return `Reporte técnico agronómico ${slugMatch[1]}-${slugMatch[2]}-${slugMatch[3]}`
  }

  return value
}

const sanitizeFileNameCharacters = (value) => {
  const invalidCharacters = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*'])

  return Array.from(String(value || ''))
    .map((character) => (
      invalidCharacters.has(character) || character.charCodeAt(0) < 32
        ? '-'
        : character
    ))
    .join('')
}

const createSafeFileName = (reportName) => {
  const base = sanitizeFileNameCharacters(
    normalizeTechnicalReportFileName(reportName || 'Reporte técnico agronómico')
  )
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*-\s*/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/[. ]+$/g, '')
    .trim()

  return `${base || 'Reporte técnico agronómico'}.pdf`
}

const waitForProgressPaint = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve())
      return
    }

    setTimeout(resolve, 0)
  })

const buildPdf = ({
  brandLogo = null,
  dateRange,
  imageCounts,
  labels,
  notes,
  reportName,
  selectedFields,
  selectedLands,
  sourceLabel = null,
  totalHa,
  visualSections = [],
}) => {
  const palette = getPdfPalette()
  const coverVisualSections = visualSections.filter((section) => section?.placement === 'cover')
  const bodyVisualSections = visualSections.filter((section) => section?.placement !== 'cover')
  const pages = [[]]
  const imageRegistry = []
  let y = 48

  const commands = () => pages[pages.length - 1]
  const hasPageContent = () => commands().length > 0
  const newPage = () => {
    pages.push([])
    y = 48
  }
  const ensureSpace = (height) => {
    if (y + height > PAGE_HEIGHT - MARGIN_BOTTOM) {
      newPage()
    }
  }

  const painter = createPainter({
    push: (op) => commands().push(op),
    palette,
    pageWidth: PAGE_WIDTH,
    pageHeight: PAGE_HEIGHT,
  })

  const registerImage = (image) => {
    if (!image?.dataUrl || !image.width || !image.height) {
      return null
    }

    const hex = getDataUrlImageHex(image.dataUrl)

    if (!hex) {
      return null
    }

    const name = `Im${imageRegistry.length + 1}`
    imageRegistry.push({...image, hex, name})

    return name
  }

  const paragraph = (value, options = {}) => {
    const x = options.x || MARGIN_X
    const width = options.width || PAGE_WIDTH - MARGIN_X * 2
    const size = options.size || 10
    const lineHeight = options.lineHeight || Math.round(size * 1.45)
    const lines = wrapText(value, width, size, options.bold)

    ensureSpace(lines.length * lineHeight + 4)
    lines.forEach((lineText) => {
      painter.text(lineText, x, y, size, {bold: options.bold, color: options.color || palette.slate})
      y += lineHeight
    })
  }

  const sectionTitle = (title, {forceNewPage = false} = {}) => {
    if (forceNewPage && hasPageContent()) {
      newPage()
    }

    ensureSpace(40)
    y = drawSectionHeader(painter, y, title)
  }

  const bullet = (value) => {
    ensureSpace(18)
    painter.text('•', MARGIN_X, y, 11, {bold: true, color: palette.primary})
    paragraph(value, {x: MARGIN_X + 16, width: PAGE_WIDTH - MARGIN_X * 2 - 16, size: 10})
  }

  const noteFigure = (item) => {
    ensureSpace(36)
    painter.text(item.title, MARGIN_X, y, 10, {bold: true, color: palette.ink})
    y += 14
    painter.text(item.subtitle || labels.unavailable || 'No disponible', MARGIN_X, y, 9, {color: palette.muted})
    y += 18
  }

  const visualFigure = (item, options = {}) => {
    if (item?.kind === 'note') {
      noteFigure(item)
      return
    }

    const imageName = registerImage(item)

    if (!imageName) {
      bullet(item.title)
      return
    }

    const cardPadding = options.cardPadding || 10
    const gapAfter = options.gapAfter ?? 16
    const cardWidth = PAGE_WIDTH - MARGIN_X * 2
    const cardHeight = options.cardHeight || VISUAL_CARD_HEIGHT

    ensureSpace(cardHeight + gapAfter)

    const ratio = Math.min((cardWidth - cardPadding * 2) / item.width, (cardHeight - cardPadding * 2) / item.height, 1)
    const imageWidth = Math.round(item.width * ratio)
    const imageHeight = Math.round(item.height * ratio)
    const cardX = MARGIN_X
    const imageX = cardX + Math.round((cardWidth - imageWidth) / 2)
    const imageY = y + Math.round((cardHeight - imageHeight) / 2)

    painter.roundedRect(cardX, y, cardWidth, cardHeight, 12, {
      fill: palette.surface,
      stroke: palette.line,
      lineWidth: 1,
    })
    painter.image(imageName, imageX, imageY, imageWidth, imageHeight)
    y += cardHeight + gapAfter
  }

  const getFigureLandKey = (item) => {
    const value = item?.landKey
    return value == null ? '' : String(value).trim()
  }

  // ----- Header band ---------------------------------------------------------
  const logoName = registerImage(brandLogo)
  const drawLogo = logoName
    ? (currentPainter, {x, top, maxWidth, maxHeight}) => {
        const ratio = Math.min(maxWidth / brandLogo.width, maxHeight / brandLogo.height, 1)
        const width = Math.round(brandLogo.width * ratio)
        const height = Math.round(brandLogo.height * ratio)
        currentPainter.image(logoName, x, top + Math.round((maxHeight - height) / 2), width, height)
      }
    : null

  y = drawHeaderBand(painter, {
    drawLogo,
    eyebrow: labels.eyebrow,
    metaLabel: labels.generatedAt,
    metaValue: labels.generatedAtValue,
    title: reportName,
  })

  // ----- Key metrics ---------------------------------------------------------
  y = drawMetricChips(painter, y, [
    {label: labels.period, value: `${dateRange.from} - ${dateRange.to}`, width: 190},
    {label: labels.surface, value: totalHa, width: 130},
    {label: labels.fields, value: selectedFields.length, width: 78},
    {label: labels.lands, value: selectedLands.length, width: 78},
  ])

  if (sourceLabel) {
    painter.text(`${labels.source}: ${sourceLabel}`, MARGIN_X, y, 9, {color: palette.muted})
    y += 18
  }

  // ----- Cover overview ------------------------------------------------------
  coverVisualSections.forEach((section) => {
    if (!section?.items?.length) {
      return
    }

    sectionTitle(section.title)
    section.items.slice(0, 1).forEach((item) => (
      visualFigure(item, {cardHeight: COVER_VISUAL_CARD_HEIGHT, gapAfter: 14})
    ))
  })

  // ----- Scope ---------------------------------------------------------------
  sectionTitle(labels.scope)
  if (selectedFields.length < 1) {
    paragraph(labels.noScope, {color: palette.muted})
  } else {
    selectedFields.forEach((field) => {
      ensureSpace(36)
      painter.text(field.name, MARGIN_X, y, 11, {bold: true, color: palette.ink})
      y += 16
      field.lands.forEach((land) => {
        paragraph(`- ${buildPdfLandName({fieldName: field.name, landName: land.name})} (${land.ha})`, {
          x: MARGIN_X + 12,
          width: PAGE_WIDTH - MARGIN_X * 2 - 12,
          size: 10,
          color: palette.muted,
        })
      })
      y += 4
    })
  }

  // ----- Visual sections / image inventory -----------------------------------
  if (bodyVisualSections.length > 0) {
    bodyVisualSections.forEach((section) => {
      if (!section?.items?.length) {
        return
      }

      sectionTitle(section.title, {forceNewPage: true})
      let previousLandKey = ''
      section.items.forEach((item) => {
        const landKey = getFigureLandKey(item)

        if (landKey && previousLandKey && landKey !== previousLandKey && hasPageContent()) {
          newPage()
        }

        visualFigure(item)

        if (landKey) {
          previousLandKey = landKey
        }
      })
    })
  } else if (imageCounts.length > 0) {
    sectionTitle(labels.images, {forceNewPage: true})
    imageCounts.forEach((item) => {
      if (Array.isArray(item.images)) {
        ensureSpace(22)
        painter.text(item.landName, MARGIN_X, y, 11, {bold: true, color: palette.ink})
        y += 16
        item.images.forEach((imageItem) => {
          const dates = Array.isArray(imageItem.dates) && imageItem.dates.length > 0
            ? imageItem.dates.join(', ')
            : imageItem.count
          bullet(`${imageItem.label}: ${dates}`)
        })
        y += 4
        return
      }

      const dates = Array.isArray(item.dates) && item.dates.length > 0
        ? item.dates.join(', ')
        : item.count
      bullet(`${item.label}: ${dates}`)
    })
  }

  // ----- Observations --------------------------------------------------------
  if (notes.observations) {
    sectionTitle(labels.observations)
    paragraph(notes.observations, {size: 10})
  }

  // ----- Footer on every page ------------------------------------------------
  pages.forEach((pageCommands, index) => {
    const pagePainter = createPainter({
      push: (op) => pageCommands.push(op),
      palette,
      pageWidth: PAGE_WIDTH,
      pageHeight: PAGE_HEIGHT,
    })
    drawFooter(pagePainter, {index, total: pages.length})
  })

  // ----- Serialize -----------------------------------------------------------
  const objects = []
  const setObject = (id, body) => {
    objects[id] = body
  }
  const nextId = () => objects.length

  setObject(1, '<< /Type /Catalog /Pages 2 0 R >>')
  setObject(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>')
  setObject(4, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>')

  imageRegistry.forEach((image) => {
    if (!image.hex) {
      return
    }

    const imageId = nextId()
    image.objectId = imageId
    setObject(
      imageId,
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${image.hex.length + 1} >>\nstream\n${image.hex}>\nendstream`
    )
  })

  const imageResource = imageRegistry
    .filter((image) => image.objectId)
    .map((image) => `/${image.name} ${image.objectId} 0 R`)
    .join(' ')
  const xObjectResource = imageResource ? `/XObject << ${imageResource} >> ` : ''

  const pageIds = []
  pages.forEach((pageCommands) => {
    const contentStream = pageCommands.join('\n')
    const contentId = nextId()
    setObject(contentId, `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`)
    const pageId = nextId()
    setObject(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> ${xObjectResource}>> /Contents ${contentId} 0 R >>`
    )
    pageIds.push(pageId)
  })

  setObject(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`
  }

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length}\n`
  pdf += '0000000000 65535 f \n'
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return pdf
}

export const downloadTechnicalReportPdf = async (report) => {
  const onProgress = typeof report.onProgress === 'function' ? report.onProgress : null
  const beforeDownload = typeof report.beforeDownload === 'function' ? report.beforeDownload : null
  const palette = getPdfPalette()
  onProgress?.(90)
  const brandLogo = await loadTenantLogoImage(palette.navy)
  onProgress?.(94)
  await waitForProgressPaint()
  const pdf = buildPdf({...report, brandLogo})
  onProgress?.(98)
  const fallbackFilename = createSafeFileName(report.reportName)
  const blob = new Blob([pdf], {type: 'application/pdf'})
  let downloadBlob = blob
  let downloadFilename = fallbackFilename

  if (beforeDownload) {
    const canDownload = await beforeDownload({
      blob,
      filename: fallbackFilename,
    })

    if (canDownload === false) {
      return false
    }

    if (canDownload?.blob) {
      downloadBlob = canDownload.blob
    }

    if (canDownload?.filename) {
      downloadFilename = canDownload.filename
    }
  }

  const url = URL.createObjectURL(downloadBlob)
  const link = document.createElement('a')

  link.href = url
  link.download = downloadFilename
  document.body.appendChild(link)
  link.click()
  link.remove()
  onProgress?.(100)
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}
